/**
 * Resolves commission report payroll month (MM/YY) from workflow message XML.
 * Used when the approvals API omits payrollMonth (legacy rows or undeployed backend).
 */
export function resolvePayrollMonthFromMessage(messageContent: string): string {
  if (!messageContent) {
    return '';
  }

  const payrollMatch = messageContent.match(/<PayrollMonth>(.*?)<\/PayrollMonth>/);
  if (payrollMatch?.[1]?.trim()) {
    return payrollMatch[1].trim();
  }

  const effectiveMatch = messageContent.match(/<EffectiveDate>(.*?)<\/EffectiveDate>/);
  if (effectiveMatch?.[1]) {
    return formatPayrollMonthYy(effectiveMatch[1].trim());
  }

  return '';
}

export function formatPayrollMonthYy(effectiveDate: string): string {
  const parsed = parsePayrollDate(effectiveDate);
  if (!parsed) {
    return '';
  }

  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const year = String(parsed.getFullYear()).slice(-2);
  return `${month}/${year}`;
}

function parsePayrollDate(value: unknown): Date | null {
  if (value == null || value === '') {
    return null;
  }

  if (value instanceof Date && !isNaN(value.getTime())) {
    return value;
  }

  const str = String(value).trim();
  if (/^\d{13}$/.test(str)) {
    const epochDate = new Date(Number(str));
    return isNaN(epochDate.getTime()) ? null : epochDate;
  }

  let date = new Date(str);
  if (!isNaN(date.getTime())) {
    return date;
  }

  const parts = str.split(/[T\s]/)[0].split('-');
  if (parts.length === 3) {
    if (parts[0].length === 4) {
      date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    } else if (parts[2].length === 4) {
      date = new Date(Number(parts[2]), Number(parts[0]) - 1, Number(parts[1]));
    }
    if (!isNaN(date.getTime())) {
      return date;
    }
  }

  const slashParts = str.split(/[T\s]/)[0].split('/');
  if (slashParts.length === 3) {
    if (slashParts[2].length === 4) {
      date = new Date(Number(slashParts[2]), Number(slashParts[0]) - 1, Number(slashParts[1]));
    } else if (slashParts[0].length === 4) {
      date = new Date(Number(slashParts[0]), Number(slashParts[1]) - 1, Number(slashParts[2]));
    }
    if (!isNaN(date.getTime())) {
      return date;
    }
  }

  return null;
}

export function enrichCommissionReportPayrollMonth<T extends { disputeType?: string; payrollMonth?: string; message_content?: string }>(
  rows: T[]
): T[] {
  return rows.map(item => {
    if (item.disputeType === 'Commission Report' && !item.payrollMonth) {
      item.payrollMonth = resolvePayrollMonthFromMessage(item.message_content || '');
    }
    return item;
  });
}

export interface CommissionReportMessageFields {
  trackingId: string;
  calRunId: string;
  salesChannel: string;
  commPlanId: string;
  effectiveDate: string;
  payrollMonth: string;
  sendToEmployeeId: string;
  submittedBy: string;
  message: string;
  totalCommission: string;
}

export function extractCommissionReportMessageField(messageContent: string, tag: string): string {
  if (!messageContent) {
    return '';
  }
  const match = messageContent.match(new RegExp(`<${tag}>(.*?)</${tag}>`, 'i'));
  return match?.[1]?.trim() || '';
}

export function parseCommissionReportMessageContent(messageContent: string): CommissionReportMessageFields {
  return {
    trackingId: extractCommissionReportMessageField(messageContent, 'TrackingID'),
    calRunId: extractCommissionReportMessageField(messageContent, 'CalRunId'),
    salesChannel: extractCommissionReportMessageField(messageContent, 'SalesChannel'),
    commPlanId: extractCommissionReportMessageField(messageContent, 'CommPlanId'),
    effectiveDate: extractCommissionReportMessageField(messageContent, 'EffectiveDate'),
    payrollMonth: extractCommissionReportMessageField(messageContent, 'PayrollMonth'),
    sendToEmployeeId: extractCommissionReportMessageField(messageContent, 'SendToEmployeeId'),
    submittedBy: extractCommissionReportMessageField(messageContent, 'SubmittedBy'),
    message: extractCommissionReportMessageField(messageContent, 'Message'),
    totalCommission: extractCommissionReportMessageField(messageContent, 'TotalCommission')
  };
}

export interface CommissionReportApprovalFilter {
  salesChannel?: string | null;
  commPlanId?: number | null;
  calRunId?: string | null;
}

export function filterCommissionReportApprovals<T extends {
  disputeType?: string;
  sales_channel?: string;
  complanid?: number | string | null;
  message_content?: string;
  trackingId?: string;
  tracking_id?: string;
  cdt?: string;
  created_dt?: string | Date;
}>(
  rows: T[],
  criteria: CommissionReportApprovalFilter
): T[] {
  return rows
    .filter(row => row.disputeType === 'Commission Report')
    .filter(row => {
      if (criteria.salesChannel && row.sales_channel !== criteria.salesChannel) {
        return false;
      }
      if (criteria.commPlanId != null && String(row.complanid ?? '') !== String(criteria.commPlanId)) {
        return false;
      }
      if (criteria.calRunId) {
        const parsed = parseCommissionReportMessageContent(row.message_content || '');
        if (String(parsed.calRunId) !== String(criteria.calRunId)) {
          return false;
        }
      }
      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(String(a.cdt || a.created_dt || '')).getTime();
      const dateB = new Date(String(b.cdt || b.created_dt || '')).getTime();
      return dateB - dateA;
    });
}

export const COMMISSION_REPORT_APPROVALS_NAV_KEYS = {
  trackingId: 'SESSION_APPROVALS_TRACKING_ID',
  disputeType: 'SESSION_APPROVALS_DISPUTE_TYPE',
  salesChannel: 'SESSION_APPROVALS_SALES_CHANNEL',
  commPlan: 'SESSION_APPROVALS_COMM_PLAN'
} as const;

export function setCommissionReportApprovalsNavigationContext(context: {
  trackingId: string;
  salesChannel?: string | null;
  commPlanId?: number | null;
}): void {
  localStorage.setItem(COMMISSION_REPORT_APPROVALS_NAV_KEYS.trackingId, context.trackingId);
  localStorage.setItem(COMMISSION_REPORT_APPROVALS_NAV_KEYS.disputeType, 'Commission Report');
  if (context.salesChannel) {
    localStorage.setItem(COMMISSION_REPORT_APPROVALS_NAV_KEYS.salesChannel, context.salesChannel);
  } else {
    localStorage.removeItem(COMMISSION_REPORT_APPROVALS_NAV_KEYS.salesChannel);
  }
  if (context.commPlanId != null) {
    localStorage.setItem(COMMISSION_REPORT_APPROVALS_NAV_KEYS.commPlan, String(context.commPlanId));
  } else {
    localStorage.removeItem(COMMISSION_REPORT_APPROVALS_NAV_KEYS.commPlan);
  }
}

export function readCommissionReportApprovalsNavigationContext(): {
  trackingId: string | null;
  disputeType: string | null;
  salesChannel: string | null;
  commPlanId: string | null;
} {
  return {
    trackingId: localStorage.getItem(COMMISSION_REPORT_APPROVALS_NAV_KEYS.trackingId),
    disputeType: localStorage.getItem(COMMISSION_REPORT_APPROVALS_NAV_KEYS.disputeType),
    salesChannel: localStorage.getItem(COMMISSION_REPORT_APPROVALS_NAV_KEYS.salesChannel),
    commPlanId: localStorage.getItem(COMMISSION_REPORT_APPROVALS_NAV_KEYS.commPlan)
  };
}

export function clearCommissionReportApprovalsNavigationContext(): void {
  Object.values(COMMISSION_REPORT_APPROVALS_NAV_KEYS).forEach(key => localStorage.removeItem(key));
}
